import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, Label, Textarea } from '@monksflow/monks-ui';
import { FormDialog } from '@/shared/ui/FormDialog';
import type { TaxonomyCampaign } from '@/entities/taxonomy-campaign';

interface EditCampaignModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaign: TaxonomyCampaign | null;
  onSave: (changes: { name: string; description: string | null }) => void;
  submitting?: boolean;
}

/**
 * Rename a campaign or change what it says it groups.
 *
 * Plain `useState` rather than react-hook-form, matching `NewTaxonomyModal`
 * beside it — this page has no RHF anywhere, and introducing it for two fields
 * would leave the two modals on different idioms.
 *
 * Names are deliberately NOT unique, so there is no duplicate check here and no
 * conflict for the server to return: the only refusal is an empty name.
 */
export function EditCampaignModal({
  open,
  onOpenChange,
  campaign,
  onSave,
  submitting = false,
}: EditCampaignModalProps) {
  const { t } = useTranslation('knowledge');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const nameFieldId = useId();
  const descriptionFieldId = useId();

  /*
   * Re-seeded when the modal OPENS rather than when `campaign` changes, so a
   * background list refetch that hands back a new object identity cannot
   * overwrite what the user has typed mid-edit.
   *
   * `campaign` is therefore left out of the dependencies ON PURPOSE — with it in,
   * a new identity while open re-runs this and does exactly what the paragraph
   * above says it cannot. Opening for a different campaign still re-seeds,
   * because `open` transitions false → true, and the caller sets the target and
   * the open flag in one handler so the target is already current by then.
   */
  useEffect(() => {
    if (open) {
      setName(campaign?.name ?? '');
      setDescription(campaign?.description ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-seed on open only, see above
  }, [open]);

  const trimmedName = name.trim();
  const canSave = Boolean(trimmedName) && !submitting;

  function handleSave() {
    if (!canSave) {
      return;
    }
    const trimmedDescription = description.trim();
    /*
     * `null`, not `''`, when the description is emptied: the column is nullable
     * and the API distinguishes "leave it" (absent) from "clear it" (null). An
     * empty string would store one and read back as a description that is there
     * but blank.
     */
    onSave({ name: trimmedName, description: trimmedDescription || null });
  }

  return (
    <FormDialog open={open} onOpenChange={onOpenChange} title={t('taxonomy.campaign.editTitle')}>
      <FormDialog.Body>
        <div className="flex flex-col gap-md">
          <div className="flex flex-col gap-xs">
            <Label htmlFor={nameFieldId}>
              {t('taxonomy.campaign.nameLabel')} <span className="text-destructive">*</span>
            </Label>
            <Input
              id={nameFieldId}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('taxonomy.campaign.namePlaceholder')}
              maxLength={255}
              className="bg-input"
            />
          </div>

          <div className="flex flex-col gap-xs">
            <Label htmlFor={descriptionFieldId}>{t('taxonomy.campaign.descriptionLabel')}</Label>
            <Textarea
              id={descriptionFieldId}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('taxonomy.campaign.descriptionPlaceholder')}
              maxLength={2000}
              rows={3}
              className="bg-input"
            />
          </div>
        </div>
      </FormDialog.Body>
      <FormDialog.Footer
        primary={{
          label: t('taxonomy.campaign.save'),
          onClick: handleSave,
          disabled: !canSave,
          loading: submitting,
        }}
        secondary={{
          label: t('taxonomy.generationList.cancel'),
          onClick: () => onOpenChange(false),
        }}
      />
    </FormDialog>
  );
}
